import useTracking from "@repo/shopify/hooks/useTracking";
import { discoverCaaEndpoints } from "@repo/shopify/utils/caaDiscovery";
import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Loading from "@atoms/Loading";

import { NOTIFICATIONS } from "@const/notifications";
import { SHOPIFY_DOMAIN, URLS } from "@const/urls";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

const CAA_CLIENT_ID = import.meta.env.VITE_JP_CAA_CLIENT_ID;

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { loginCaa } = useCustomerStore();
  const { openAccount, openToast } = useLayoutStore();
  const { gtmLogin } = useTracking();

  // React StrictMode의 effect 이중 실행으로 authorization code가 두 번 소진되는 것을 방지
  const hasRun = useRef(false);

  /** id_token(JWT)의 payload만 디코드 — 서명 검증은 하지 않고 GTM 트래킹용 customer id만 뽑아 씀 */
  const decodeJwtSub = (idToken: string): string | undefined => {
    try {
      const payload = JSON.parse(atob(idToken.split(".")[1])) as { sub?: unknown };
      return typeof payload.sub === "string" ? payload.sub.replace("gid://shopify/Customer/", "") : undefined;
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const exchangeCode = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const codeVerifier = sessionStorage.getItem("caaCodeVerifier");
      const expectedState = sessionStorage.getItem("caaState");

      sessionStorage.removeItem("caaCodeVerifier");
      sessionStorage.removeItem("caaState");

      if (!code || !state || !codeVerifier || state !== expectedState) {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        navigate(URLS.HOME);
        return;
      }

      try {
        const { tokenEndpoint } = await discoverCaaEndpoints(JP_SHOP_DOMAIN);

        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            client_id: CAA_CLIENT_ID,
            redirect_uri: `${window.location.origin}${URLS.AUTH_CALLBACK}`,
            code,
            code_verifier: codeVerifier,
          }),
        });

        if (!response.ok) throw new Error(`[CAA] token exchange failed: ${response.status}`);

        const data = (await response.json()) as {
          access_token: string;
          refresh_token: string;
          id_token: string;
          expires_in: number;
        };

        loginCaa({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          idToken: data.id_token,
          expiresAt: new Date(Date.now() + data.expires_in * 1000).toISOString(),
        });

        gtmLogin("email", decodeJwtSub(data.id_token));

        const loginAfterUrl = sessionStorage.getItem("loginAfterUrl");
        sessionStorage.removeItem("loginAfterUrl");

        navigate(loginAfterUrl || URLS.HOME);
        openAccount(false);
        openToast(NOTIFICATIONS.SUCCESS_LOGIN);
      } catch {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        navigate(URLS.HOME);
      }
    };

    void exchangeCode();
  }, []);

  return <Loading type="page" />;
}
